/**
 * CoPilot — Operation Co-Pilot page.
 *
 * Full-page chat interface with generative UI blocks.
 * Shows a welcome screen with suggested operations before first message,
 * then switches to a conversation view with AI-generated UI blocks.
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { AppShell } from '../../components/AppShell'
import { BlockRenderer } from '../../components/copilot/BlockRenderer'
import {
  conversationAtom,
  inputAtom,
  isLoadingAtom,
  errorAtom,
  hasStartedAtom,
  sendMessageAtom,
  resetConversationAtom,
  SUGGESTED_OPERATIONS,
  type CoPilotMessage,
  type SuggestedOperation,
} from '../../stores/CoPilotStore'
import {
  fetchedAtom as listingsFetchedAtom,
  fetchListingsAtom,
} from '../../stores/AmazonListingsStore'
import {
  fetchedAtom as ordersFetchedAtom,
  fetchOrdersAtom,
} from '../../stores/AmazonOrdersStore'

// ─── Icons ──────────────────────────────────────────────────────────────────

function SparkleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0L9.79 6.21L16 8L9.79 9.79L8 16L6.21 9.79L0 8L6.21 6.21L8 0Z" />
    </svg>
  )
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
    </svg>
  )
}

function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
    </svg>
  )
}

// Operation card icons
function ChartMiniIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
    </svg>
  )
}

function SearchMiniIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  )
}

function OrdersMiniIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 1 0-7.5 0v4.5m11.356-1.993 1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 0 1-1.12-1.243l1.264-12A1.125 1.125 0 0 1 5.513 7.5h12.974c.576 0 1.059.435 1.119 1.007ZM8.625 10.5a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm7.5 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
    </svg>
  )
}

function DollarMiniIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  )
}

function DocumentMiniIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  )
}

const operationIcons: Record<string, typeof ChartMiniIcon> = {
  chart: ChartMiniIcon,
  search: SearchMiniIcon,
  orders: OrdersMiniIcon,
  dollar: DollarMiniIcon,
  document: DocumentMiniIcon,
}

// ─── Welcome Screen ──────────────────────────────────────────────────────────

function WelcomeScreen({ onSend }: { onSend: (text: string) => void }) {
  return (
    <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-[#F5F5F5] mb-4">
          <SparkleIcon className="size-5 text-[#A1A1AA]" />
        </div>
        <h2 className="text-xl font-semibold text-[#18181B] mb-2">
          Welcome to CoPilot
        </h2>
        <p className="text-[13px] text-[#71717A] mb-8">
          Your operations intelligence assistant. Ask about your business
          performance, get actionable insights, and identify issues.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {SUGGESTED_OPERATIONS.map((op) => (
            <OperationCard key={op.label} operation={op} onClick={() => onSend(op.prompt)} />
          ))}
        </div>
      </div>
    </div>
  )
}

function OperationCard({
  operation,
  onClick,
}: {
  operation: SuggestedOperation
  onClick: () => void
}) {
  const Icon = operationIcons[operation.icon] || ChartMiniIcon
  return (
    <button
      onClick={onClick}
      className="text-left p-4 border border-[#E5E5E5] rounded-xl hover:bg-[#FAFAFA] hover:border-[#D4D4D8] transition-colors cursor-pointer group"
    >
      <Icon className="size-5 text-[#A1A1AA] mb-2 group-hover:text-[#71717A] transition-colors" />
      <div className="text-[13px] font-medium text-[#18181B] mb-0.5">
        {operation.label}
      </div>
      <div className="text-[11px] text-[#A1A1AA] leading-snug">
        {operation.description}
      </div>
    </button>
  )
}

// ─── Thinking Steps ─────────────────────────────────────────────────────────

const THINKING_STEPS = [
  { label: 'Understanding your request', icon: 'brain' },
  { label: 'Analyzing your data', icon: 'chart' },
  { label: 'Generating insights', icon: 'sparkle' },
] as const

function ThinkingIndicator() {
  const [activeStep, setActiveStep] = useState(0)

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev < THINKING_STEPS.length - 1 ? prev + 1 : prev))
    }, 2800)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="pl-10 space-y-1">
      {THINKING_STEPS.map((step, i) => {
        if (i > activeStep) return null
        const isActive = i === activeStep
        const isDone = i < activeStep

        return (
          <div
            key={step.label}
            className="flex items-center gap-2"
            style={{
              opacity: 0,
              animation: 'fadeSlideIn 0.3s ease-out forwards',
            }}
          >
            {isDone ? (
              <svg
                className="size-3.5 text-[#A1A1AA] shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m4.5 12.75 6 6 9-13.5"
                />
              </svg>
            ) : (
              <div className="size-1.5 bg-[#71717A] rounded-full animate-pulse shrink-0" />
            )}
            <span
              className={`text-[13px] ${
                isActive ? 'text-[#52525B]' : 'text-[#A1A1AA]'
              }`}
            >
              {step.label}
              {isActive && '...'}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ─── Conversation View ───────────────────────────────────────────────────────

function ConversationView() {
  const conversation = useAtomValue(conversationAtom)
  const isLoading = useAtomValue(isLoadingAtom)
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastResponseRef = useRef<HTMLDivElement>(null)
  const prevIsLoadingRef = useRef(false)
  const streamScrolledRef = useRef(false)

  // Determine if streaming content has started (last message is assistant while still loading)
  const lastMsg = conversation[conversation.length - 1]
  const isStreaming = isLoading && lastMsg?.role === 'assistant'
  // Show thinking indicator only while waiting (before any content arrives)
  const showThinking = isLoading && !isStreaming

  useEffect(() => {
    const wasLoading = prevIsLoadingRef.current
    prevIsLoadingRef.current = isLoading

    if (showThinking) {
      // Waiting phase: scroll to bottom so user sees thinking indicator
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      }
      streamScrolledRef.current = false
    } else if (isStreaming && !streamScrolledRef.current) {
      // First streaming content arrived: scroll to top of response ONCE
      streamScrolledRef.current = true
      requestAnimationFrame(() => {
        lastResponseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } else if (wasLoading && !isLoading) {
      // Stream complete: reset scroll tracking
      streamScrolledRef.current = false
    }
  }, [conversation, isLoading, showThinking, isStreaming])

  // Find the last assistant message index to attach the ref
  let lastAssistantIdx = -1
  for (let i = conversation.length - 1; i >= 0; i--) {
    if (conversation[i].role === 'assistant') {
      lastAssistantIdx = i
      break
    }
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-6">
      {conversation.map((msg, idx) => {
        if (msg.role === 'user') {
          return (
            <div key={msg.id} className="flex justify-end">
              <div className="max-w-[80%] bg-[#F5F5F5] rounded-2xl rounded-br-md px-4 py-2.5">
                <p className="text-[14px] text-[#18181B] whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          )
        }

        // Assistant message with blocks
        const assistantMsg = msg as CoPilotMessage
        const isLast = idx === lastAssistantIdx
        return (
          <div
            key={msg.id}
            ref={isLast ? lastResponseRef : undefined}
            className="flex gap-3"
          >
            <div className="size-7 rounded-full bg-[#F5F5F5] flex items-center justify-center shrink-0 mt-0.5">
              <SparkleIcon className="size-3 text-[#A1A1AA]" />
            </div>
            <div className="flex-1 min-w-0 max-w-[90%]">
              <BlockRenderer blocks={assistantMsg.blocks} />
              {assistantMsg.toolsUsed && assistantMsg.toolsUsed.length > 0 && (
                <div className="mt-2 text-[10px] text-[#D4D4D8]">
                  Tools: {assistantMsg.toolsUsed.join(', ')}
                </div>
              )}
            </div>
          </div>
        )
      })}

      {/* Thinking indicator — hidden once streaming content arrives */}
      {showThinking && <ThinkingIndicator />}
    </div>
  )
}

// ─── Input Bar ───────────────────────────────────────────────────────────────

function InputBar({ onSend }: { onSend: (text: string) => void }) {
  const input = useAtomValue(inputAtom)
  const setInput = useSetAtom(inputAtom)
  const isLoading = useAtomValue(isLoadingAtom)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const handleSend = useCallback(() => {
    if (!input.trim() || isLoading) return
    onSend(input)
  }, [input, isLoading, onSend])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend]
  )

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`
    }
  }, [input])

  return (
    <div className="bg-white px-6 py-4">
      <div className="max-w-3xl mx-auto">
        <div className="relative border border-[#D4D4D8] rounded-2xl bg-white shadow-sm focus-within:border-[#A1A1AA] focus-within:shadow-md transition-all">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about your business..."
            disabled={isLoading}
            rows={2}
            className="w-full resize-none bg-transparent px-4 pt-3 pb-12 text-[14px] text-[#18181B] placeholder-[#71717A] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <div className="absolute bottom-2.5 right-2.5">
            <button
              onClick={handleSend}
              disabled={!input.trim() || isLoading}
              className="size-8 flex items-center justify-center rounded-lg bg-[#18181B] text-white hover:bg-[#27272A] disabled:bg-[#E5E5E5] disabled:text-[#A1A1AA] disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <SendIcon className="size-4" />
            </button>
          </div>
        </div>
        <p className="text-[11px] text-[#A1A1AA] text-center mt-2">
          CoPilot may produce inaccurate information. Verify important details.
        </p>
      </div>
    </div>
  )
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function CoPilot() {
  const hasStarted = useAtomValue(hasStartedAtom)
  const error = useAtomValue(errorAtom)
  const sendMessage = useSetAtom(sendMessageAtom)
  const resetConversation = useSetAtom(resetConversationAtom)
  const listingsFetched = useAtomValue(listingsFetchedAtom)
  const fetchListings = useSetAtom(fetchListingsAtom)
  const ordersFetched = useAtomValue(ordersFetchedAtom)
  const fetchOrders = useSetAtom(fetchOrdersAtom)

  // Pre-fetch listings and orders data if not already cached
  useEffect(() => {
    if (!listingsFetched) fetchListings()
    if (!ordersFetched) fetchOrders()
  }, [listingsFetched, fetchListings, ordersFetched, fetchOrders])

  const handleSend = useCallback(
    (text: string) => {
      sendMessage(text)
    },
    [sendMessage]
  )

  return (
    <AppShell activeNav="copilot">
      <div className="flex-1 flex flex-col min-h-0">
        {/* Header */}
        <div className="px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <SparkleIcon className="size-4 text-[#A1A1AA]" />
            <h1 className="text-[15px] font-semibold text-[#18181B]">CoPilot</h1>
            <span className="text-[11px] text-[#A1A1AA] bg-[#F5F5F5] px-2 py-0.5 rounded-full font-medium">
              Beta
            </span>
          </div>
          {hasStarted && (
            <button
              onClick={() => resetConversation()}
              className="flex items-center gap-1.5 text-[12px] text-[#71717A] hover:text-[#18181B] transition-colors cursor-pointer"
            >
              <RefreshIcon className="size-3.5" />
              New conversation
            </button>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-6 mt-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg px-4 py-2.5 text-[13px] text-[#991B1B]">
            {error}
          </div>
        )}

        {/* Content */}
        {hasStarted ? <ConversationView /> : <WelcomeScreen onSend={handleSend} />}

        {/* Input */}
        <InputBar onSend={handleSend} />
      </div>
    </AppShell>
  )
}
